import React from 'react';
import { Info } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';

/*
 * One stock vocabulary for Inventory, Reorder and Stock Health.
 *
 *   Out of stock  available <= 0                               (backend "CRITICAL" on Inventory and Reorder)
 *   Low cover     7 days of cover or less (Inventory "LOW"),
 *                 or under 14 days with incoming covering it   (Reorder "LOW_STOCK")
 *   Watch         8-14 days of cover                            (Inventory "WATCH" only)
 *   Reorder       stock + incoming below required stock         (Reorder "REORDER_NEEDED")
 *   Healthy       15-90 days of cover                           (Inventory "OK", Reorder "HEALTHY")
 *   Overstock     more than 90 days of cover                    (Inventory "OVERSTOCK")
 *
 * Stock Health classifies by ageing (days of stock), which is a different
 * question, so it uses its own "Aging" labels and never says "Critical".
 * A stocked-out row there shows the shared "Out of stock" badge instead.
 */

// Inventory page: backend `health` values.
export const INVENTORY_HEALTH = {
  CRITICAL: { label: 'Out of stock', tone: 'danger', rule: 'No units available' },
  LOW: { label: 'Low cover', tone: 'warning', rule: '7 days of cover or less' },
  WATCH: { label: 'Watch', tone: 'warning', rule: '8-14 days of cover' },
  OK: { label: 'Healthy', tone: 'success', rule: '15-90 days of cover' },
  OVERSTOCK: { label: 'Overstock', tone: 'info', rule: 'More than 90 days of cover' },
};

// Reorder page: backend `status` values.
export const REORDER_STATUS = {
  CRITICAL: { label: 'Out of stock', tone: 'danger', rule: 'No units available' },
  REORDER_NEEDED: { label: 'Reorder', tone: 'warning', rule: 'Stock + incoming below required stock' },
  LOW_STOCK: { label: 'Low cover', tone: 'warning', rule: 'Under 14 days of cover, incoming covers it' },
  HEALTHY: { label: 'Healthy', tone: 'success', rule: '14+ days of cover' },
};

// Stock Health page: backend `classification` values (aging, not stock-outs).
export const AGING_CLASS = {
  Active: { label: 'Fast moving', tone: 'success', rule: '7 days of stock or less, in stock (or retail-only with an open PO)' },
  Slow: { label: 'Slow', tone: 'info', rule: '8-30 days of stock' },
  'Very Slow': { label: 'Very slow', tone: 'warning', rule: '31-60 days of stock' },
  Critical: { label: 'Aging', tone: 'danger', rule: '61-90 days of stock' },
  Dead: { label: 'Non-moving', tone: 'neutral', rule: 'More than 90 days of stock, or no sales' },
};

// Shared out-of-stock definition, used to override a status badge on any page.
export const OUT_OF_STOCK = { label: 'Out of stock', tone: 'danger', rule: 'No units available' };

// Backends use 999 (or the string '999+') both for "no sales" and for
// "cover of 999 days or more". Only sales velocity can tell them apart.
const DAYS_CAP_SENTINEL = 999;
const LARGE_COVER_SORT = 1e9;

const isOut = (available) => available != null && Number(available) <= 0;

/** Normalise a days-of-stock value. Returns null when it is missing or the 999 sentinel. */
export const daysValue = (days) => {
  if (days == null || days === '' || days === '999+') return null;
  const n = Number(days);
  if (!Number.isFinite(n) || n >= DAYS_CAP_SENTINEL) return null;
  return n;
};

/*
 * Kind of a days value:
 *   'out'     no stock
 *   'days'    a real number of days
 *   'large'   cover at or beyond the cap (sales > 0, or sentinel with unknown sales)
 *   'nosales' stock on hand but no sales
 * `velocity` (units/day) is optional; pass it whenever the endpoint returns it.
 */
const daysKind = (days, { available, velocity } = {}) => {
  if (isOut(available)) return 'out';
  const hasVelocity = velocity != null && velocity !== '' && Number.isFinite(Number(velocity));
  // A real number below the sentinel always means sales > 0 (velocity may be rounded to 0).
  if (daysValue(days) != null) return 'days';
  if (hasVelocity && Number(velocity) <= 0) return 'nosales';
  if (hasVelocity) return 'large';
  // Unknown velocity: the 999 sentinel is ambiguous, a missing value means no sales.
  return days == null || days === '' ? 'nosales' : 'large';
};

/**
 * "12 days", "365+ days", "Out of stock" or "No sales".
 * Pass `available` so a stocked-out SKU never reads as "No sales", and
 * `velocity` so a slow mover with lots of stock never reads as "No sales".
 */
export const formatDaysOfStock = (days, { available, velocity, cap = 365, short = false } = {}) => {
  const kind = daysKind(days, { available, velocity });
  if (kind === 'out') return short ? '0d' : 'Out of stock';
  if (kind === 'nosales') return 'No sales';
  const unit = (n) => (short ? 'd' : (n === 1 ? ' day' : ' days'));
  if (kind === 'large') return `${cap}+${unit(cap)}`;
  const n = daysValue(days);
  if (n > cap) return `${cap}+${unit(cap)}`;
  return `${Math.round(n * 10) / 10}${unit(n)}`;
};

/** Tooltip for a days-of-stock cell. */
export const daysOfStockTitle = (days, { available, velocity } = {}) => {
  const kind = daysKind(days, { available, velocity });
  if (kind === 'out') return 'Out of stock';
  if (kind === 'nosales') return 'No sales in the period, so cover cannot be calculated';
  if (kind === 'large') {
    return velocity != null ? 'Cover of 999 days or more at current sales' : '999+ days of stock, or no sales';
  }
  return `${daysValue(days)} days of cover at current sales`;
};

/** Sort key for days of stock: out of stock first, large cover high, no sales last. */
export const daysSortValue = (days, available, velocity) => {
  const kind = daysKind(days, { available, velocity });
  if (kind === 'out') return 0;
  if (kind === 'nosales') return Number.MAX_SAFE_INTEGER;
  if (kind === 'large') return LARGE_COVER_SORT;
  return daysValue(days);
};

/** Text colour for a days-of-cover number (700 shades for contrast). */
export const daysCoverClass = (days, available, velocity) => {
  const kind = daysKind(days, { available, velocity });
  if (kind === 'out') return 'text-red-700 font-semibold';
  if (kind === 'nosales') return 'text-gray-500';
  if (kind === 'large') return 'text-violet-700';
  const n = daysValue(days);
  if (n <= 7) return 'text-red-700 font-semibold';
  if (n <= 14) return 'text-amber-700';
  return n > 90 ? 'text-violet-700' : 'text-green-700';
};

/** Badge for a status from one of the maps above. Hover shows the threshold. */
export const StockStatusBadge = ({ status, map, ...props }) => {
  const def = map[status];
  return (
    <StatusBadge
      status={status}
      label={def?.label}
      tone={def?.tone}
      title={def?.rule}
      {...props}
    />
  );
};

/** Small "What do these mean?" legend listing every status and its threshold. */
export const StockLegend = ({ map, title = 'Status thresholds', className = '', testId = 'stock-legend' }) => (
  <details className={`group text-xs text-gray-600 ${className}`} data-testid={testId}>
    <summary className="inline-flex cursor-pointer select-none items-center gap-1 font-medium text-gray-600 hover:text-gray-900">
      <Info className="h-3.5 w-3.5" aria-hidden="true" /> {title}
    </summary>
    <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
      {Object.entries(map).map(([key, def]) => (
        <li key={key} className="flex items-center gap-1.5">
          <StatusBadge status={key} label={def.label} tone={def.tone} />
          <span>{def.rule}</span>
        </li>
      ))}
    </ul>
  </details>
);
